import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import { coy, duotoneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useDataSync } from '../context/DataSyncContext';
import type { DiscussionMessage, Flow, FlowPreview, HistoryEntry } from '../types';
import '../styles/flow.css';

SyntaxHighlighter.registerLanguage?.('json', json);

const flowPreviewCache = new Map<string, FlowPreview>();
const FLOW_TOKEN_REGEX = /\[\[flow:([a-f0-9-]+)\]\]/gi;
const FLOW_URL_REGEX =
  /(?:https?:\/\/[^\s]+)?\/flows\/([a-f0-9-]{8,})(?=[^a-z0-9-]|$)/gi;

interface ListingLocation {
  pathname: string;
  search?: string;
  label?: string;
}

function normalizeFlowLinks(input: string): string {
  if (!input) {
    return '';
  }
  return input.replace(FLOW_URL_REGEX, (_match, id: string) => `[[flow:${id}]]`);
}

function denormalizeFlowLinks(input: string): string {
  if (!input) {
    return '';
  }
  return input.replace(FLOW_TOKEN_REGEX, (_match, id: string) => `/flows/${id}`);
}

function toPreviewFromFlow(flow: Flow): FlowPreview {
  return {
    id: flow.id,
    title: flow.title,
    description: flow.description,
    kind: flow.kind,
    tags: flow.tags ?? [],
    status: flow.status,
    updatedAt: flow.updatedAt,
    createdByName: flow.createdByName,
    flowType: flow.flowType ?? null,
    flowTypeDetail: flow.flowTypeDetail ?? null,
    image: flow.image ?? null,
    upvoteCount: typeof flow.upvoteCount === 'number' ? flow.upvoteCount : 0,
    isRecommended: Boolean(flow.isRecommended),
    recommendedAt: flow.recommendedAt ?? null,
    recommendedByName: flow.recommendedByName ?? null,
    autoRecommended: flow.autoRecommended ?? false,
    madeByThales: Boolean(flow.madeByThales)
  };
}

interface FlowReferencePreviewProps {
  flowId: string;
  currentFlow: Flow | null;
  listingLocation?: ListingLocation | null;
}

function FlowReferenceLink({ flowId, currentFlow, listingLocation }: FlowReferencePreviewProps) {
  const [preview, setPreview] = useState<FlowPreview | null>(() => {
    if (currentFlow && currentFlow.id === flowId) {
      const currentPreview = toPreviewFromFlow(currentFlow);
      flowPreviewCache.set(flowId, currentPreview);
      return currentPreview;
    }
    return flowPreviewCache.get(flowId) || null;
  });
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;

    const cached = flowPreviewCache.get(flowId);
    if (cached) {
      setPreview(cached);
      setLoading(false);
      setError(null);
      return () => {
        isActive = false;
      };
    }

    if (currentFlow && currentFlow.id === flowId) {
      const currentPreview = toPreviewFromFlow(currentFlow);
      flowPreviewCache.set(flowId, currentPreview);
      setPreview(currentPreview);
      setLoading(false);
      setError(null);
      return () => {
        isActive = false;
      };
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const response = await api.flowPreview(flowId);
        if (!isActive) {
          return;
        }
        flowPreviewCache.set(flowId, response);
        setPreview(response);
        setError(null);
      } catch (err) {
        if (isActive) {
          setError((err as Error).message);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [flowId, currentFlow]);

  if (loading) {
    return <span className="discussion-flow-link discussion-flow-link--loading">Loading entry…</span>;
  }

  if (error) {
    return (
      <span className="discussion-flow-link discussion-flow-link--error">
        Entry unavailable
      </span>
    );
  }

  if (!preview) {
    return null;
  }

  return (
    <Link
      to={`/flows/${preview.id}`}
      className="discussion-flow-link"
      title={preview.title}
      state={listingLocation ? { from: listingLocation } : undefined}
    >
      {preview.title}
    </Link>
  );
}

export function FlowDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { refreshAllBadges } = useDataSync();
  const [flow, setFlow] = useState<Flow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [moderationNotes, setModerationNotes] = useState('');
  const [deleteAfterReject, setDeleteAfterReject] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<{ message: string; tone: 'success' | 'error' } | null>(
    null
  );
  const [shareFeedback, setShareFeedback] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);
  const [discussionMessage, setDiscussionMessage] = useState('');
  const [discussionError, setDiscussionError] = useState<string | null>(null);
  const [discussionSubmitting, setDiscussionSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentValue, setEditingCommentValue] = useState('');
  const [editingCommentError, setEditingCommentError] = useState<string | null>(null);
  const [editingSubmitting, setEditingSubmitting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [historyCopyFeedback, setHistoryCopyFeedback] = useState<{ version: number; message: string } | null>(null);
  const [upvoteSubmitting, setUpvoteSubmitting] = useState(false);
  const [recommendSubmitting, setRecommendSubmitting] = useState(false);
  const [watchSubmitting, setWatchSubmitting] = useState(false);
  const [thalesSubmitting, setThalesSubmitting] = useState(false);
  const listingLocationRef = useRef<ListingLocation | null>(null);
  const copyTimeoutRef = useRef<number | null>(null);
  const shareTimeoutRef = useRef<number | null>(null);
  const historyCopyTimeoutRef = useRef<number | null>(null);
  const discussionTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const locationState = location.state as { from?: ListingLocation } | null;
  const candidateListing = locationState?.from;
  if (
    candidateListing &&
    (listingLocationRef.current?.pathname !== candidateListing.pathname ||
      listingLocationRef.current?.search !== candidateListing.search ||
      listingLocationRef.current?.label !== candidateListing.label)
  ) {
    listingLocationRef.current = candidateListing;
  }
  const listingLocation = listingLocationRef.current;

  useEffect(() => {
    if (!id) {
      return;
    }

    let isActive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const payload = await api.flow(id);
        if (isActive) {
          setFlow(payload);
        }
      } catch (err) {
        if (isActive) {
          setError((err as Error).message);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [id]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      if (shareTimeoutRef.current) {
        window.clearTimeout(shareTimeoutRef.current);
      }
      if (historyCopyTimeoutRef.current) {
        window.clearTimeout(historyCopyTimeoutRef.current);
      }
    };
  }, []);

  const scheduleCopyReset = () => {
    if (copyTimeoutRef.current) {
      window.clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = window.setTimeout(() => {
      setCopyFeedback(null);
    }, 2500);
  };

  const scheduleShareReset = () => {
    if (shareTimeoutRef.current) {
      window.clearTimeout(shareTimeoutRef.current);
    }
    shareTimeoutRef.current = window.setTimeout(() => {
      setShareFeedback(null);
    }, 2500);
  };

  const scheduleHistoryCopyReset = () => {
    if (historyCopyTimeoutRef.current) {
      window.clearTimeout(historyCopyTimeoutRef.current);
    }
    historyCopyTimeoutRef.current = window.setTimeout(() => {
      setHistoryCopyFeedback(null);
    }, 2500);
  };

  const handleToggleUpvote = useCallback(async () => {
    if (!flow) {
      return;
    }
    setUpvoteSubmitting(true);
    try {
      const result = flow.viewerHasUpvoted
        ? await api.removeFlowUpvote(flow.id)
        : await api.upvoteFlow(flow.id);
      setFlow((current) =>
        current
          ? {
              ...current,
              upvoteCount: result.upvoteCount,
              viewerHasUpvoted: result.viewerHasUpvoted
            }
          : current
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUpvoteSubmitting(false);
    }
  }, [flow, setFlow, setError]);

  const handleToggleRecommendation = useCallback(async () => {
    if (!flow || user?.role !== 'admin') {
      return;
    }

    if (flow.isRecommended) {
      const confirmRemoval = window.confirm('Remove the recommendation label from this entry?');
      if (!confirmRemoval) {
        return;
      }
    }

    setRecommendSubmitting(true);
    try {
      const response = flow.isRecommended
        ? await api.clearRecommendation(flow.id)
        : await api.recommendFlow(flow.id);
      setFlow(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRecommendSubmitting(false);
    }
  }, [flow, user, setFlow, setError]);

  const handleToggleWatch = useCallback(async () => {
    if (!flow) {
      return;
    }
    setWatchSubmitting(true);
    try {
      if (flow.viewerIsWatcher) {
        await api.unwatchFlow(flow.id);
        setFlow((current) => {
          if (!current) {
            return current;
          }
          const roles = current.viewerNotificationRoles || {
            created: false,
            participated: false,
            watched: false
          };
          return {
            ...current,
            viewerIsWatcher: false,
            viewerNotificationRoles: { ...roles, watched: false }
          };
        });
      } else {
        await api.watchFlow(flow.id);
        setFlow((current) => {
          if (!current) {
            return current;
          }
          const roles = current.viewerNotificationRoles || {
            created: false,
            participated: false,
            watched: false
          };
          return {
            ...current,
            viewerIsWatcher: true,
            viewerNotificationRoles: { ...roles, watched: true }
          };
        });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWatchSubmitting(false);
    }
  }, [flow, setFlow, setError]);

  const handleToggleMadeByThales = useCallback(async () => {
    if (!flow || user?.role !== 'admin') {
      return;
    }

    if (flow.madeByThales) {
      const confirmRemoval = window.confirm('Remove the “Made by Thales” label from this entry?');
      if (!confirmRemoval) {
        return;
      }
    }

    setThalesSubmitting(true);
    try {
      const response = flow.madeByThales
        ? await api.clearMadeByThales(flow.id)
        : await api.markMadeByThales(flow.id);
      setFlow(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setThalesSubmitting(false);
    }
  }, [flow, user, setFlow, setError]);

  const handleCopy = async () => {
    if (!flow?.json) {
      return;
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(flow.json, null, 2));
      void api
        .recordAnalyticsEvent({
          type: 'flow_copy',
          flowId: flow.id,
          metadata: { variant: 'json' }
        })
        .catch(() => {
          /* ignore analytics failures */
        });
      setCopyFeedback({ message: 'Copied!', tone: 'success' });
      scheduleCopyReset();
    } catch (clipError) {
      console.error('Unable to copy to clipboard', clipError);
      setCopyFeedback({ message: 'Copy failed', tone: 'error' });
      scheduleCopyReset();
    }
  };

  const handleCopyEntryLink = async () => {
    if (!flow) {
      return;
    }
    try {
      const origin = window.location.origin || '';
      const link = `${origin.replace(/\/+$/, '')}/flows/${flow.id}`;
      await navigator.clipboard.writeText(link);
      void api
        .recordAnalyticsEvent({
          type: 'flow_copy',
          flowId: flow.id,
          metadata: { variant: 'link' }
        })
        .catch(() => {
          /* ignore analytics failures */
        });
      setShareFeedback({ message: 'Link copied!', tone: 'success' });
      scheduleShareReset();
    } catch (clipError) {
      console.error('Unable to copy entry link', clipError);
      setShareFeedback({ message: 'Copy failed', tone: 'error' });
      scheduleShareReset();
    }
  };

  const handleDownload = async () => {
    if (!flow) {
      return;
    }
    try {
      const data = await api.exportFlow(flow.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${flow.title.replace(/\s+/g, '-')}.json`;
      link.click();
      URL.revokeObjectURL(url);
      void api
        .recordAnalyticsEvent({
          type: 'flow_download',
          flowId: flow.id,
          metadata: { format: 'json' }
        })
        .catch(() => {
          /* ignore analytics failures */
        });
    } catch (exportError) {
      console.error('Failed to export flow', exportError);
    }
  };

  const beginEditComment = useCallback((entry: DiscussionMessage) => {
    setEditingCommentId(entry.id);
    setEditingCommentValue(denormalizeFlowLinks(entry.message));
    setEditingCommentError(null);
  }, []);

  const cancelEditComment = useCallback(() => {
    setEditingCommentId(null);
    setEditingCommentValue('');
    setEditingCommentError(null);
  }, []);

  const submitEditComment = useCallback(async () => {
    if (!flow || !editingCommentId) {
      return;
    }

    const trimmed = editingCommentValue.trim();
    const normalized = normalizeFlowLinks(trimmed);
    if (!normalized) {
      setEditingCommentError('Comment cannot be empty.');
      return;
    }

    setEditingSubmitting(true);
    try {
      const updated = await api.updateDiscussionComment(flow.id, editingCommentId, normalized);
      setFlow((current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          discussion: current.discussion.map((entry) =>
            entry.id === updated.id ? updated : entry
          )
        };
      });
      cancelEditComment();
    } catch (err) {
      setEditingCommentError((err as Error).message);
    } finally {
      setEditingSubmitting(false);
    }
  }, [flow, editingCommentId, editingCommentValue, cancelEditComment]);

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      if (!flow) {
        return;
      }
      const confirmDelete = window.confirm('Delete this comment? This action cannot be undone.');
      if (!confirmDelete) {
        return;
      }
      setDeletingCommentId(commentId);
      try {
        await api.deleteDiscussionComment(flow.id, commentId);
        setFlow((current) => {
          if (!current) {
            return current;
          }
          return {
            ...current,
            discussion: current.discussion.filter((entry) => entry.id !== commentId)
          };
        });
        if (editingCommentId === commentId) {
          cancelEditComment();
        }
      } catch (err) {
        console.error('Failed to delete comment', err);
      } finally {
        setDeletingCommentId(null);
      }
    },
    [flow, editingCommentId, cancelEditComment]
  );

  useEffect(() => {
    cancelEditComment();
  }, [flow?.id, cancelEditComment]);

  const handleApprove = async () => {
    if (!flow) {
      return;
    }
    setApproving(true);
    setError(null);
    try {
      const updated = await api.approveFlow(flow.id, moderationNotes || undefined);
      setFlow(updated);
      void refreshAllBadges();
    } catch (approveError) {
      setError((approveError as Error).message);
    } finally {
      setApproving(false);
      setModerationNotes('');
    }
  };

  const handleReject = async () => {
    if (!flow) {
      return;
    }

    setRejecting(true);
    setError(null);

    try {
      const result = await api.rejectFlow(flow.id, {
        notes: moderationNotes || undefined,
        deleteAfterReject
      });

      if (!result) {
        setFlow(null);
        void refreshAllBadges();
        navigate('/admin/pending', { replace: true });
        return;
      }

      setFlow(result as Flow);
      void refreshAllBadges();
    } catch (rejectError) {
      setError((rejectError as Error).message);
    } finally {
      setRejecting(false);
      setModerationNotes('');
      setDeleteAfterReject(false);
    }
  };

  const handleDiscussionSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!flow) {
      return;
    }

    const trimmed = discussionMessage.trim();
    const normalizedMessage = normalizeFlowLinks(trimmed);
    if (!normalizedMessage) {
      setDiscussionError('Please enter a message before sending.');
      return;
    }

    setDiscussionSubmitting(true);
    setDiscussionError(null);

    try {
      const response = await api.addDiscussionComment(flow.id, normalizedMessage);
      setFlow((current) =>
        current
          ? {
              ...current,
              discussion: [...current.discussion, response]
            }
          : current
      );
      setDiscussionMessage('');
      setDiscussionError(null);
    } catch (commentError) {
      setDiscussionError((commentError as Error).message);
    } finally {
      setDiscussionSubmitting(false);
    }
  };

  const handleHistoryCopy = async (entry: HistoryEntry) => {
    const historyJson = entry.json ? JSON.stringify(entry.json, null, 2) : null;
    if (!historyJson) {
      setHistoryCopyFeedback({ version: entry.version, message: 'No JSON to copy' });
      scheduleHistoryCopyReset();
      return;
    }

    try {
      await navigator.clipboard.writeText(historyJson);
      setHistoryCopyFeedback({ version: entry.version, message: 'Copied!' });
    } catch {
      setHistoryCopyFeedback({ version: entry.version, message: 'Copy failed' });
    }
    scheduleHistoryCopyReset();
  };

  const canEdit = user && flow && (user.role === 'admin' || flow.createdBy === user.id);
  const canDelete = user?.role === 'admin' && flow;
  const canApprove = user?.role === 'admin' && flow?.status === 'pending';
  const canReject = user?.role === 'admin' && flow?.status === 'pending';

  const handleDelete = async () => {
    if (!flow) return;
    if (!window.confirm(`Delete "${flow.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.deleteFlow(flow.id);
      navigate('/');
    } catch (err) {
      setError((err as Error).message);
      setDeleting(false);
    }
  };
  const canModerate = canApprove || canReject;
  const canViewDiscussion = Boolean(flow && user);
  const canComment = Boolean(user);
  const isRejected = flow?.status === 'rejected';
  const isPublishedView = Boolean(flow?.isPublishedView);
  const publishedVersion = flow?.publishedVersion ?? null;
  const publishedUpdatedAt = flow?.publishedUpdatedAt ?? null;

  useEffect(() => {
    if (!canReject) {
      setDeleteAfterReject(false);
    }
  }, [canReject]);

  useEffect(() => {
    setHistoryCopyFeedback(null);
  }, [flow?.id]);

  const syntaxTheme = useMemo(() => (theme === 'dark' ? duotoneDark : coy), [theme]);
  const syntaxStyle = useMemo(
    () => ({
      margin: 0,
      borderRadius: '0 0 16px 16px',
      background: theme === 'dark' ? '#101a33' : '#0f172a',
      color: theme === 'dark' ? '#e5e9f0' : '#e2e8f0',
      fontSize: '0.85rem',
      padding: '1.5rem'
    }),
    [theme]
  );

  const renderTextSegment = useCallback((segment: string, key: string) => {
    const lines = segment.split('\n');
    return (
      <span key={key}>
        {lines.map((line, index) => (
          <span key={`${key}-${index}`} className="discussion-entry__text-line">
            {line}
            {index < lines.length - 1 ? <br /> : null}
          </span>
        ))}
      </span>
    );
  }, []);

  const renderDiscussionContent = useCallback(
    (entry: DiscussionMessage): ReactNode[] => {
      const nodes: ReactNode[] = [];
      const withTokens = normalizeFlowLinks(entry.message || '');
      const tokenRegex = /\[\[flow:([a-f0-9-]+)\]\]/gi;
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = tokenRegex.exec(withTokens)) !== null) {
        if (match.index > lastIndex) {
          const segment = withTokens.slice(lastIndex, match.index);
          nodes.push(renderTextSegment(segment, `${entry.id}-segment-${lastIndex}`));
        }
        const referenceId = match[1];
        nodes.push(
          <FlowReferenceLink
            key={`${entry.id}-ref-${referenceId}-${match.index}`}
            flowId={referenceId}
            currentFlow={flow}
            listingLocation={listingLocation}
          />
        );
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < withTokens.length) {
        const segment = withTokens.slice(lastIndex);
        nodes.push(renderTextSegment(segment, `${entry.id}-segment-tail`));
      }

      if (nodes.length === 0) {
        nodes.push(renderTextSegment(withTokens, `${entry.id}-segment-full`));
      }

      return nodes;
    },
    [flow, listingLocation, renderTextSegment]
  );

  const listingLinkTo = listingLocation
    ? `${listingLocation.pathname}${listingLocation.search ?? ''}`
    : '/';
  const listingLabel = listingLocation?.label ?? 'Marketplace';

  if (loading) {
    return <div className="loading">Loading entry...</div>;
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
        <div>
          <Link to={listingLinkTo}>Back to {listingLabel}</Link>
        </div>
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="empty-state">
        <h3>Entry not found</h3>
        <Link to={listingLinkTo}>Return to {listingLabel}</Link>
      </div>
    );
  }

  const isNodeEntry = flow.kind === 'node';
  const isListDefinition =
    flow.json &&
    typeof flow.json === 'object' &&
    !Array.isArray(flow.json) &&
    Array.isArray((flow.json as { list?: unknown[] }).list);
  const allowCopyJson = isNodeEntry || isListDefinition;
  const allowDownloadJson = !allowCopyJson;
  const classificationTitle = isNodeEntry ? 'Node classification' : 'Flow classification';
  const definitionHeading = isNodeEntry ? 'Node definition' : 'Flow definition';
  const displayVersion = isPublishedView && publishedVersion ? publishedVersion : flow.version;
  const publishedUpdatedDisplay =
    isPublishedView && publishedUpdatedAt
      ? new Date(publishedUpdatedAt).toLocaleString()
      : null;
  const hasPrerequisites = typeof flow.prerequisites === 'string' && flow.prerequisites.length > 0;
  const prerequisiteText = hasPrerequisites ? flow.prerequisites ?? '' : '';
  const isAdminUser = user?.role === 'admin';
  const viewerHasUpvoted = Boolean(flow.viewerHasUpvoted);
  const upvoteCount = typeof flow.upvoteCount === 'number' ? flow.upvoteCount : 0;
  const notificationRoles = flow.viewerNotificationRoles;
  const recommendedByName = flow.recommendedByName || 'Unknown';
  const recommendedTimestamp =
    flow.isRecommended && flow.recommendedAt ? new Date(flow.recommendedAt).toLocaleString() : null;
  const recommendationButtonLabel = flow.isRecommended ? 'Remove recommendation' : 'Mark as recommended';
  const madeByThalesButtonLabel = flow.madeByThales
    ? 'Clear “Made by Thales”'
    : 'Mark as Made by Thales';
  const madeByThalesMeta = flow.madeByThales
    ? `Made by Thales${flow.madeByThalesByName ? ` • Set by ${flow.madeByThalesByName}` : ''}${
        flow.madeByThalesAt ? ` on ${new Date(flow.madeByThalesAt).toLocaleString()}` : ''
      }`
    : 'Not marked as made by Thales.';
  const isWatchingFlow = Boolean(flow.viewerIsWatcher);
  const watchButtonLabel = isWatchingFlow ? 'Unwatch' : 'Watch';

  return (
    <div className="flow-detail-page">
      <nav className="flow-detail__breadcrumbs" aria-label="Breadcrumb">
        <Link to={listingLinkTo} className="flow-detail__breadcrumb-link">
          {listingLabel}
        </Link>
        <span className="flow-detail__breadcrumb-separator">/</span>
        <span className="flow-detail__breadcrumb-current" aria-current="page">
          {flow.title}
        </span>
      </nav>

      <div className="flow-detail">
        <div className="flow-detail__main">
          <div className="flow-detail__header">
            <div className="flow-detail__title-row">
              <h1>{flow.title}</h1>
              <span
                className={`flow-detail__badge flow-detail__badge--kind flow-detail__badge--${
                  flow.kind === 'node' ? 'node' : 'flow'
                }`}
              >
                {flow.kind === 'node' ? 'Node' : 'Flow'}
              </span>
              <span
                className={`flow-detail__badge flow-detail__badge--visibility flow-detail__badge--${
                  flow.visibility === 'team_restricted' ? 'team' : 'public'
                }`}
                title={
                  flow.visibility === 'team_restricted' && flow.visibleTeams?.length
                    ? `Visible to: ${flow.visibleTeams.map((team) => team.name).join(', ')}`
                    : 'Visible to everyone'
                }
              >
                {flow.visibility === 'team_restricted'
                  ? flow.visibleTeams?.map((team) => team.name).join(', ') || 'Team restricted'
                  : 'Public'}
              </span>
              {flow.isRecommended ? (
                <span className="flow-detail__badge flow-detail__badge--recommended">Recommended</span>
              ) : null}
              {flow.madeByThales ? (
                <span className="flow-detail__badge flow-detail__badge--thales">Made by Thales</span>
              ) : null}
            </div>
          <div className="flow-detail__header-actions">
            <button
              type="button"
              className={`button button-secondary flow-detail__upvote-button${
                viewerHasUpvoted ? ' flow-detail__upvote-button--active' : ''
              }`}
              onClick={handleToggleUpvote}
              disabled={upvoteSubmitting}
              aria-pressed={viewerHasUpvoted}
            >
              {upvoteSubmitting ? 'Working…' : viewerHasUpvoted ? 'Upvoted' : 'Upvote'}
            </button>
            <span className="flow-detail__upvote-count">
              {upvoteCount.toLocaleString()} {upvoteCount === 1 ? 'upvote' : 'upvotes'}
            </span>
            <button
              type="button"
              className={`button-tertiary flow-detail__watch-button${
                isWatchingFlow ? ' flow-detail__watch-button--active' : ''
              }`}
              onClick={handleToggleWatch}
              disabled={watchSubmitting}
            >
              {watchSubmitting
                ? isWatchingFlow
                  ? 'Unwatching…'
                  : 'Watching…'
                : watchButtonLabel}
            </button>
            {isAdminUser ? (
              <button
                type="button"
                className="button-tertiary flow-detail__recommend-button"
                onClick={handleToggleRecommendation}
                disabled={recommendSubmitting}
              >
                {recommendSubmitting
                  ? flow.isRecommended
                    ? 'Removing…'
                    : 'Recommending…'
                  : recommendationButtonLabel}
              </button>
            ) : null}
            {isAdminUser ? (
              <button
                type="button"
                className="button-tertiary flow-detail__thales-button"
                onClick={handleToggleMadeByThales}
                disabled={thalesSubmitting}
              >
                {thalesSubmitting
                  ? flow.madeByThales
                    ? 'Clearing…'
                    : 'Flagging…'
                  : madeByThalesButtonLabel}
              </button>
            ) : null}
            {flow.isRecommended ? (
              <span className="flow-detail__recommended-detail">
                Recommended by {recommendedByName}
                {recommendedTimestamp ? ` on ${recommendedTimestamp}` : ''}
              </span>
            ) : null}
            {flow.madeByThales ? (
              <span className="flow-detail__thales-detail">
                Made by Thales
                {flow.madeByThalesByName ? ` • ${flow.madeByThalesByName}` : ''}
                {flow.madeByThalesAt ? ` on ${new Date(flow.madeByThalesAt).toLocaleString()}` : ''}
              </span>
            ) : null}
          </div>
          <p className="flow-detail__description">
            {flow.description || 'No description provided yet.'}
          </p>
        </div>

        <div className="flow-detail__meta">
          <div>
            <h4>Ownership</h4>
            <p>
              Author: <strong>{flow.createdByName}</strong>
            </p>
            <p>Created: {new Date(flow.createdAt).toLocaleString()}</p>
            <p>Updated: {new Date(flow.updatedAt).toLocaleString()}</p>
          </div>
          <div>
            <h4>{classificationTitle}</h4>
            <p>
              <strong>{isNodeEntry ? 'Node' : 'Flow'}</strong>
            </p>
            {flow.flowType ? (
              <p>
                <strong>{flow.flowType}</strong>
                {flow.flowTypeDetail ? <> — {flow.flowTypeDetail}</> : null}
              </p>
            ) : isNodeEntry ? (
              <p>This node is reusable across flows and is not categorized.</p>
            ) : null}
          </div>
          <div>
            <h4>Status</h4>
            <p>
              <span className={`status status-${flow.status}`}>{flow.status}</span>
            </p>
            <p>Version: {displayVersion}</p>
            {flow.lastApprovedByName ? (
              <p>
                Last approval: {flow.lastApprovedByName} on{' '}
                {flow.lastApprovedAt ? new Date(flow.lastApprovedAt).toLocaleString() : ''}
              </p>
            ) : (
              <p>Not yet approved</p>
            )}
          </div>
          <div>
            <h4>Tags</h4>
            <div className="flow-detail__tags">
              {flow.tags?.map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <div>
            <h4>Thales label</h4>
            <p>{madeByThalesMeta}</p>
          </div>
          {notificationRoles ? (
            <div>
              <h4>Notifications</h4>
              <ul className="flow-detail__notification-list">
                <li className={notificationRoles.created ? 'on' : 'off'}>
                  Created entries: {notificationRoles.created ? 'On' : 'Off'}
                </li>
                <li className={notificationRoles.participated ? 'on' : 'off'}>
                  Participated entries: {notificationRoles.participated ? 'On' : 'Off'}
                </li>
                <li className={notificationRoles.watched ? 'on' : 'off'}>
                  Watched entries: {notificationRoles.watched ? 'On' : 'Off'}
                </li>
              </ul>
            </div>
          ) : null}
        </div>

        {hasPrerequisites ? (
          <section className="flow-detail__prerequisites">
            <h3>Prerequisites</h3>
            <p className="flow-detail__prerequisites-text">{prerequisiteText}</p>
          </section>
        ) : null}

        {isPublishedView ? (
          <div className="flow-detail__alert flow-detail__alert--published">
            {isRejected
              ? `Recent updates to this ${isNodeEntry ? 'node' : 'flow'} were rejected.`
              : `Updates to this ${isNodeEntry ? 'node' : 'flow'} are awaiting approval.`}{' '}
            You&apos;re viewing the last approved revision
            {publishedUpdatedDisplay ? ` from ${publishedUpdatedDisplay}` : ''}.
          </div>
        ) : null}

        {isRejected ? (
          <div className="flow-detail__alert flow-detail__alert--rejected">
            This submission was rejected. Review the discussion below, address the feedback, and
            resubmit to trigger another review.
          </div>
        ) : null}

        {canModerate ? (
          <div className="flow-detail__moderation">
            <h3>Moderator notes</h3>
            <p className="flow-detail__moderation-help">
              Share context with the author before approving or rejecting. Messages are visible to the
              author and other admins.
            </p>
            <textarea
              placeholder="Optional message to include with your decision"
              value={moderationNotes}
              onChange={(event) => setModerationNotes(event.target.value)}
            />
            <div className="flow-detail__moderation-actions">
              {canApprove ? (
                <button
                  className="button"
                  type="button"
                  onClick={handleApprove}
                  disabled={approving || rejecting}
                >
                  {approving ? 'Approving…' : 'Approve'}
                </button>
              ) : null}
              {canReject ? (
                <button
                  className="button button-secondary"
                  type="button"
                  onClick={handleReject}
                  disabled={rejecting || approving}
                >
                  {rejecting ? 'Rejecting…' : 'Reject'}
                </button>
              ) : null}
            </div>
            {canReject ? (
              <label className="flow-detail__moderation-option">
                <input
                  type="checkbox"
                  checked={deleteAfterReject}
                  onChange={(event) => setDeleteAfterReject(event.target.checked)}
                  disabled={rejecting || approving}
                />
                Delete this submission after rejecting
              </label>
            ) : null}
          </div>
        ) : null}

        <section className="flow-detail__json">
          <details>
            <summary className="flow-detail__json-header">
              <h3>{definitionHeading}</h3>
              <span className="code-chip">JSON</span>
            </summary>
            <SyntaxHighlighter
              language="json"
              style={syntaxTheme}
              customStyle={syntaxStyle}
              showLineNumbers
            >
              {JSON.stringify(flow.json, null, 2)}
            </SyntaxHighlighter>
          </details>
        </section>

        <section className="flow-detail__history">
          <h3>Revision history</h3>
          {flow.history && flow.history.length > 0 ? (
            <ul className="history-list">
              {flow.history.map((entry) => {
                const historyJson = entry.json ? JSON.stringify(entry.json, null, 2) : null;
                const feedbackMessage =
                  historyCopyFeedback?.version === entry.version ? historyCopyFeedback.message : null;

                return (
                  <li key={entry.version} className="history-entry">
                    <details>
                      <summary>
                        <span>
                          Version {entry.version} — Updated by {entry.updatedByName || 'Unknown'} on{' '}
                          {new Date(entry.updatedAt).toLocaleString()}
                        </span>
                        {entry.notes ? <span className="history-entry__note">{entry.notes}</span> : null}
                      </summary>
                      <div className="history-entry__body">
                        <div className="history-entry__toolbar">
                          <button
                            className="button button-tertiary"
                            type="button"
                            onClick={() => handleHistoryCopy(entry)}
                            disabled={!historyJson}
                          >
                            Copy JSON
                          </button>
                          {feedbackMessage ? (
                            <span className="history-entry__feedback">{feedbackMessage}</span>
                          ) : null}
                        </div>
                        {entry.prerequisites ? (
                          <div className="history-entry__prerequisites">
                            <h4>Prerequisites</h4>
                            <p className="history-entry__prerequisites-text">
                              {entry.prerequisites}
                            </p>
                          </div>
                        ) : null}
                        {historyJson ? (
                          <SyntaxHighlighter
                            language="json"
                            style={syntaxTheme}
                            customStyle={syntaxStyle}
                            showLineNumbers
                          >
                            {historyJson}
                          </SyntaxHighlighter>
                        ) : (
                          <p className="history-entry__empty">No JSON snapshot captured for this version.</p>
                        )}
                      </div>
                    </details>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>No revision history available yet.</p>
          )}
        </section>

        {flow.activities && flow.activities.length > 0 ? (
          <section className="flow-detail__activities">
            <h3>Activity log</h3>
            <ul className="activity-list">
              {flow.activities.map((activity) => {
                const message =
                  activity.message && activity.message.trim().length > 0
                    ? activity.message
                    : activity.type === 'recommendation-set'
                    ? 'Marked as recommended'
                    : activity.type === 'recommendation-cleared'
                    ? 'Recommendation removed'
                    : activity.type === 'made-by-thales-set'
                    ? 'Marked as made by Thales'
                    : activity.type === 'made-by-thales-cleared'
                    ? '“Made by Thales” label removed'
                    : 'Activity recorded';
                const timestamp = activity.performedAt
                  ? new Date(activity.performedAt).toLocaleString()
                  : '';
                return (
                  <li key={activity.id} className="activity-list__item">
                    <div className="activity-list__summary">
                      <span className="activity-list__message">{message}</span>
                      <span className="activity-list__meta">
                        {activity.performedByName} {timestamp ? `· ${timestamp}` : ''}
                      </span>
                    </div>
                    {activity.note ? (
                      <p className="activity-list__note">Note: {activity.note}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        <section className="flow-detail__approvals">
          <h3>Approvals</h3>
          {flow.approvals.length === 0 ? (
            <p>No approvals captured yet.</p>
          ) : (
            <ul>
              {flow.approvals.map((approval) => (
                <li key={approval.id}>
                  <strong>{approval.approvedByName}</strong> on{' '}
                  {new Date(approval.approvedAt).toLocaleString()}
                  {approval.notes ? <> — {approval.notes}</> : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        {canViewDiscussion ? (
          <section className="flow-detail__discussion">
            <h3>Review discussion</h3>
            {flow.discussion.length === 0 ? (
              <p>No comments yet.</p>
            ) : (
              <ul className="discussion-list">
                {flow.discussion.map((entry) => {
                  const isEditing = editingCommentId === entry.id;
                  const canEditEntry = user && (user.role === 'admin' || user.id === entry.authorId);
                  const canDeleteEntry = Boolean(isAdminUser);
                  const editedTimestamp = entry.isEdited ? entry.editedAt || entry.updatedAt : null;

                  return (
                    <li key={entry.id} className="discussion-entry">
                      <div className="discussion-entry__header">
                        <div className="discussion-entry__meta">
                          <strong>{entry.authorName}</strong>
                          <span> on {new Date(entry.createdAt).toLocaleString()}</span>
                          {entry.isEdited && editedTimestamp ? (
                            <span className="discussion-entry__edited">
                              · Edited {new Date(editedTimestamp).toLocaleString()} by {entry.editedByName}
                            </span>
                          ) : null}
                        </div>
                        <div className="discussion-entry__actions">
                          {canEditEntry ? (
                            <button
                              type="button"
                              className="button-tertiary"
                              onClick={() =>
                                isEditing ? cancelEditComment() : beginEditComment(entry)
                              }
                              disabled={editingSubmitting && isEditing}
                            >
                              {isEditing ? 'Cancel edit' : 'Edit'}
                            </button>
                          ) : null}
                          {canDeleteEntry ? (
                            <button
                              type="button"
                              className="button-tertiary button-tertiary-danger"
                              onClick={() => handleDeleteComment(entry.id)}
                              disabled={deletingCommentId === entry.id}
                            >
                              {deletingCommentId === entry.id ? 'Deleting…' : 'Delete'}
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="discussion-entry__body">
                        {isEditing ? (
                          <div className="discussion-entry__editor">
                            <textarea
                              value={editingCommentValue}
                              onChange={(event) => {
                                setEditingCommentValue(event.target.value);
                                if (editingCommentError) {
                                  setEditingCommentError(null);
                                }
                              }}
                              rows={4}
                            />
                            {editingCommentError ? (
                              <p className="form-error">{editingCommentError}</p>
                            ) : null}
                            <div className="discussion-entry__editor-actions">
                              <button
                                type="button"
                                className="button button-secondary"
                                onClick={submitEditComment}
                                disabled={editingSubmitting}
                              >
                                {editingSubmitting ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                type="button"
                                className="button-tertiary"
                                onClick={cancelEditComment}
                                disabled={editingSubmitting}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="discussion-entry__content">
                            {renderDiscussionContent(entry)}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {canComment ? (
              <form className="discussion-form" onSubmit={handleDiscussionSubmit}>
                <textarea
                  ref={discussionTextareaRef}
                  placeholder="Collaborate here to resolve outstanding questions"
                  value={discussionMessage}
                  onChange={(event) => {
                    setDiscussionMessage(event.target.value);
                    if (discussionError) {
                      setDiscussionError(null);
                    }
                  }}
                  rows={4}
                />
                {discussionError ? <p className="form-error">{discussionError}</p> : null}
                <div className="discussion-form__actions">
                  <button
                    className="button button-secondary"
                    type="submit"
                    disabled={discussionSubmitting}
                  >
                    {discussionSubmitting ? 'Sending…' : 'Post message'}
                  </button>
                </div>
              </form>
            ) : null}
          </section>
        ) : null}
      </div>

      <aside className="flow-detail__actions">
        {allowCopyJson ? (
          <button className="button button-secondary" type="button" onClick={handleCopy}>
            Copy JSON
          </button>
        ) : null}
        {copyFeedback && allowCopyJson ? (
          <span className={`copy-feedback copy-feedback-${copyFeedback.tone}`}>
            {copyFeedback.message}
          </span>
        ) : null}
        <button className="button button-secondary" type="button" onClick={handleCopyEntryLink}>
          Copy entry link
        </button>
        {shareFeedback ? (
          <span className={`copy-feedback copy-feedback-${shareFeedback.tone}`}>
            {shareFeedback.message}
          </span>
        ) : null}
        {allowDownloadJson ? (
          <button className="button button-secondary" type="button" onClick={handleDownload}>
            Download JSON
          </button>
        ) : null}
        {canEdit ? (
          <button
            className="button"
            type="button"
            onClick={() => navigate(`/flows/${flow.id}/edit`)}
          >
            {`Edit ${isNodeEntry ? 'Node' : 'Flow'}`}
          </button>
        ) : null}
        {flow && flow.visibility === 'team_restricted' && user ? (
          <button
            className="button"
            type="button"
            onClick={() =>
              navigate('/flows/new', {
                state: {
                  prefill: {
                    title: flow.title,
                    description: flow.description,
                    prerequisites: flow.prerequisites ?? '',
                    kind: flow.kind,
                    tags: flow.tags || [],
                    json: flow.json ?? {},
                  }
                }
              })
            }
          >
            Submit to Marketplace
          </button>
        ) : null}
        {canDelete ? (
          <button
            className="button button-danger"
            type="button"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : `Delete ${isNodeEntry ? 'Node' : 'Flow'}`}
          </button>
        ) : null}
      </aside>
    </div>
    </div>
  );
}
